import { ArrowLeft, ArrowRight, Edit2, Building2, Palette, MapPin, Phone, Briefcase, TrendingUp, Image, Sun } from 'lucide-react';
import type { OnboardingStepData, OnboardingCustomer } from '../../../types/onboarding';

interface Step10Props {
  stepData: OnboardingStepData;
  customer: OnboardingCustomer;
  onBack: () => void;
  onSubmit: () => void;
  onEditStep: (step: number) => void;
  saving: boolean;
  submitting: boolean;
}

export function Step10Review({
  stepData,
  customer,
  onBack,
  onSubmit,
  onEditStep,
  saving,
}: Step10Props) {
  const handleSubmit = () => {
    // onSubmit now goes to Step 11 (AI Profile)
    onSubmit();
  };

  // Extract data from stepData
  const step1 = stepData.step1;
  const step3 = stepData.step3;
  const step4 = stepData.step4;
  const step5 = stepData.step5;
  const step6 = stepData.step6;
  const step7 = stepData.step7;
  const step8 = stepData.step8;
  const step9 = stepData.step9;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Review Your Information
        </h1>
        <p className="text-gray-600">
          Make sure all your details are correct before we create your brand snapshot
        </p>
      </div>

      {/* Review Sections - Ordered by UI step */}
      <div className="space-y-6">
        {/* Industry Details - UI Step 1 */}
        <ReviewSection
          icon={<Briefcase className="w-5 h-5" />}
          title="Industry"
          onEdit={() => onEditStep(1)}
        >
          <div className="space-y-2">
            {step7?.industry_category && (
              <p>
                <span className="text-gray-500">Category:</span>{' '}
                <span className="font-medium capitalize">{step7.industry_category.replace(/-/g, ' ')}</span>
              </p>
            )}
            {step7?.industry_type && (
              <p>
                <span className="text-gray-500">Type:</span>{' '}
                <span className="font-medium capitalize">{step7.industry_type.replace(/-/g, ' ')}</span>
              </p>
            )}
            {!step7?.industry_category && (
              <p className="text-gray-400 italic">No industry selected</p>
            )}
          </div>
        </ReviewSection>

        {/* Business Details - UI Step 2 */}
        <ReviewSection
          icon={<Building2 className="w-5 h-5" />}
          title="Business Details"
          onEdit={() => onEditStep(2)}
        >
          <div className="space-y-2">
            <p>
              <span className="text-gray-500">Business Name:</span>{' '}
              <span className="font-medium">{customer.businessName || 'Not provided'}</span>
            </p>
            {step4?.tagline && (
              <p>
                <span className="text-gray-500">Tagline:</span>{' '}
                <span className="font-medium">{step4.tagline}</span>
              </p>
            )}
            {step4?.business_description && (
              <p>
                <span className="text-gray-500">Description:</span>{' '}
                <span className="font-medium line-clamp-2">{step4.business_description}</span>
              </p>
            )}
            {step4?.services && (
              <p>
                <span className="text-gray-500">Services:</span>{' '}
                <span className="font-medium">{step4.services}</span>
              </p>
            )}
          </div>
        </ReviewSection>

        {/* Brand Assets - UI Step 3 */}
        <ReviewSection
          icon={<Palette className="w-5 h-5" />}
          title="Brand Assets"
          onEdit={() => onEditStep(3)}
        >
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <span className="text-gray-500">Logo:</span>
              {step3?.logo_url ? (
                <img
                  src={step3.logo_url}
                  alt="Business logo"
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <span className="text-gray-400 italic">Not provided</span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <span className="text-gray-500">Colors:</span>
              {step3?.primary_color && (
                <div className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded-full border border-gray-200"
                    style={{ backgroundColor: step3.primary_color }}
                  />
                  <span className="text-sm">{step3.primary_color}</span>
                </div>
              )}
              {step3?.secondary_color && (
                <div className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded-full border border-gray-200"
                    style={{ backgroundColor: step3.secondary_color }}
                  />
                  <span className="text-sm">{step3.secondary_color}</span>
                </div>
              )}
              {!step3?.primary_color && !step3?.secondary_color && (
                <span className="text-gray-400 italic">Using defaults</span>
              )}
            </div>
          </div>
        </ReviewSection>

        {/* Theme & Tone - UI Step 4 */}
        <ReviewSection
          icon={<Sun className="w-5 h-5" />}
          title="Theme & Tone"
          onEdit={() => onEditStep(4)}
        >
          <div className="space-y-2">
            <p>
              <span className="text-gray-500">Theme:</span>{' '}
              <span className="font-medium capitalize">{step1?.themeMode || 'Light'}</span>
            </p>
            {step1?.tone && (
              <p>
                <span className="text-gray-500">Voice:</span>{' '}
                <span className="font-medium capitalize">{step1.tone.replace('-', ' ')}</span>
              </p>
            )}
            {step1?.priorities && step1.priorities.length > 0 && (
              <p>
                <span className="text-gray-500">Priorities:</span>{' '}
                <span className="font-medium capitalize">{step1.priorities.join(', ')}</span>
              </p>
            )}
          </div>
        </ReviewSection>

        {/* Location & Hours - UI Step 5 */}
        <ReviewSection
          icon={<MapPin className="w-5 h-5" />}
          title="Location & Hours"
          onEdit={() => onEditStep(5)}
        >
          <div className="space-y-2">
            {step5?.street_address && (
              <p>
                <span className="text-gray-500">Address:</span>{' '}
                <span className="font-medium">
                  {step5.street_address}, {step5.city}, {step5.state} {step5.zip_code}
                </span>
              </p>
            )}
            {step5?.hours && (
              <div>
                <span className="text-gray-500">Hours:</span>{' '}
                <span className="font-medium">
                  {Object.entries(step5.hours).filter(([, h]) => !h.closed).length} days open
                </span>
              </div>
            )}
          </div>
        </ReviewSection>

        {/* Contact & Social - UI Step 6 */}
        <ReviewSection
          icon={<Phone className="w-5 h-5" />}
          title="Contact & Social"
          onEdit={() => onEditStep(6)}
        >
          <div className="space-y-2">
            {step6?.phone && (
              <p>
                <span className="text-gray-500">Phone:</span>{' '}
                <span className="font-medium">{step6.phone}</span>
              </p>
            )}
            {step6?.email && (
              <p>
                <span className="text-gray-500">Email:</span>{' '}
                <span className="font-medium">{step6.email}</span>
              </p>
            )}
            {(step6?.facebook || step6?.instagram || step6?.linkedin) && (
              <p>
                <span className="text-gray-500">Social:</span>{' '}
                <span className="font-medium">
                  {[
                    step6.facebook && 'Facebook',
                    step6.instagram && 'Instagram',
                    step6.linkedin && 'LinkedIn',
                    step6.youtube && 'YouTube',
                  ].filter(Boolean).join(', ')}
                </span>
              </p>
            )}
          </div>
        </ReviewSection>

        {/* Your Story - UI Step 7 */}
        <ReviewSection
          icon={<TrendingUp className="w-5 h-5" />}
          title="Your Story"
          onEdit={() => onEditStep(7)}
        >
          <div className="space-y-2">
            {step8?.stats && step8.stats.length > 0 ? (
              <p>
                <span className="text-gray-500">Stats:</span>{' '}
                <span className="font-medium">
                  {step8.stats.length} stat{step8.stats.length !== 1 ? 's' : ''} configured
                </span>
              </p>
            ) : (
              <p className="text-gray-400 italic">No stats added</p>
            )}
            {step8?.credentials && step8.credentials.length > 0 && (
              <p>
                <span className="text-gray-500">Credentials:</span>{' '}
                <span className="font-medium">
                  {step8.credentials.map(c => c.name).join(', ')}
                </span>
              </p>
            )}
            {step8?.testimonials && step8.testimonials.length > 0 ? (
              <p>
                <span className="text-gray-500">Testimonials:</span>{' '}
                <span className="font-medium">
                  {step8.testimonials.length} testimonial{step8.testimonials.length !== 1 ? 's' : ''}
                </span>
              </p>
            ) : step8?.use_placeholder_testimonials ? (
              <p>
                <span className="text-gray-500">Testimonials:</span>{' '}
                <span className="font-medium">Using placeholders</span>
              </p>
            ) : null}
          </div>
        </ReviewSection>

        {/* Gallery & Photos - UI Step 8 */}
        <ReviewSection
          icon={<Image className="w-5 h-5" />}
          title="Gallery & Photos"
          onEdit={() => onEditStep(8)}
        >
          <div className="space-y-2">
            <p>
              <span className="text-gray-500">Hero Image:</span>{' '}
              <span className="font-medium capitalize">
                {step9?.hero_image_choice === 'upload' && step9?.hero_image_url
                  ? 'Uploaded'
                  : step9?.hero_image_choice === 'stock'
                  ? 'Using stock image'
                  : step9?.hero_image_choice === 'skip'
                  ? 'Skipped'
                  : 'Not set'}
              </span>
            </p>
            <p>
              <span className="text-gray-500">Gallery:</span>{' '}
              <span className="font-medium">
                {step9?.gallery_choice === 'upload' && step9?.gallery_images?.length > 0
                  ? `${step9.gallery_images.length} photo${step9.gallery_images.length !== 1 ? 's' : ''} uploaded`
                  : step9?.gallery_choice === 'stock'
                  ? 'Using stock images'
                  : step9?.gallery_choice === 'skip'
                  ? 'Skipped'
                  : 'Not set'}
              </span>
            </p>
          </div>
        </ReviewSection>
      </div>

      {/* Info Box */}
      <div className="bg-primary-50 rounded-xl p-4 border border-primary-100">
        <p className="text-sm text-primary-800">
          Please verify that all the information above is correct. In the next step, our AI will
          create a brand profile summary to ensure we truly understand your business before building your site.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex items-center gap-2 px-8 py-3 rounded-lg font-medium transition-all bg-primary-600 text-white hover:bg-primary-700"
        >
          Yes, All Info Is Correct - See My Brand Snapshot
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Helper component for review sections
function ReviewSection({
  icon,
  title,
  onEdit,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-gray-900">
          {icon}
          <h3 className="font-semibold">{title}</h3>
        </div>
        <button
          onClick={onEdit}
          className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
        >
          <Edit2 className="w-4 h-4" />
          Edit
        </button>
      </div>
      <div className="text-sm">{children}</div>
    </div>
  );
}
